import {
	ActionList,
	ActionListBox,
	ActionListCollapsibleSection,
	ActionListItem,
	FloatingActionMenu,
	FontIconName,
	SvgIconValue
} from "client/Components";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { useCreationHandler, useGlobalCreationItems } from "./hooks";
import { GlobalCreationItem, GlobalCreationItemKind } from "client/Api/GlobalCreation/globalCreation";
import { MainMenuItem, useMainMenuItemActivationHandlers } from "../Layout/mainMenuStore";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { groupBy } from "client_legacy/ndaw/arrayHelpers";
import { getGlobalCreationItemConfiguration } from "./common";
import { never } from "client_legacy/ndaw/errors";
import { localize } from "client_legacy/ndaw/culture";
import { categoryConfigurations } from "./PlusMenu";

export const ItemCreationFab = (): ReactElement => {
	const {
		deactivate
	} = useMainMenuItemActivationHandlers(MainMenuItem.CreateItem);

	const [openedElement, openCreationItemHandler] = useCreationHandler();

	const allItems: readonly CreationListItem[] | undefined = useGlobalCreationItems(converter);

	const clickHandler = useCallback(
		(item: CreationListItem): void => {
			deactivate();
			openCreationItemHandler(item.originalItem);
		},
		[deactivate, openCreationItemHandler]
	);

	const categoriesToRender = useMemo(
		(): readonly CategoryToRender[] => {
			if (!allItems) {
				return [];
			}

			const categories: Partial<Record<GlobalCreationItemKind, readonly CreationListItem[]>> = groupBy(
				allItems,
				(x) => x.originalItem
					.kind
			);

			return categoryConfigurations
				.map(
					(x): CategoryToRender | null => {
						const items: readonly CreationListItem[] | undefined = categories[x.kind];
						if (!items) {
							return null;
						}

						return {
							...x,
							items
						};
					}
				)
				.filter(isNotNil);
		},
		[allItems]
	);

	return (
		<>
			<FloatingActionMenu trackingId={trackingId}>
				<ActionListBox
					hasBusyIndicator={isNil(allItems)}
					menuTitle={localize("_Create")}
					trackingId={generateTrackingId(trackingId, "__dropover")}
				>
					<ActionList width="fill">
						{categoriesToRender.map((g): ReactElement => (
							<ActionListCollapsibleSection
								header={g.name}
								trackingId={g.trackingId}
								key={g.trackingId}
								isExpandedByDefault
							>
								{g.items.map((x): ReactElement => (
									<ActionListItem
										key={x.trackingId}
										icon={x.icon}
										text={x.name}
										onClick={() => clickHandler(x)}
									/>
								))}
							</ActionListCollapsibleSection>
						))}
					</ActionList>
				</ActionListBox>
			</FloatingActionMenu>

			{openedElement}
		</>
	);
};
ItemCreationFab.displayName = "ItemCreationFab";

const trackingId = "fab-global-item-creation";

type CategoryToRender = {
	kind: GlobalCreationItemKind;
	name: string;
	trackingId: string;
	items: readonly CreationListItem[];
};

type CreationListItem = {
	name: string;
	icon: FontIconName | SvgIconValue | undefined;
	trackingId: string;
	originalItem: GlobalCreationItem;
};

function converter(item: GlobalCreationItem): CreationListItem {
	const {
		nameKey,
		icon: originalIcon,
		trackingId
	} = getGlobalCreationItemConfiguration(item);

	let icon: FontIconName | SvgIconValue | undefined;
	switch (originalIcon.kind) {
		case "svg":
			icon = originalIcon;
			break;
		case "font":
			icon = originalIcon.name;
			break;
		default:
			never(originalIcon);
	}

	return {
		name: localize(nameKey),
		icon,
		trackingId,
		originalItem: item
	};
}
